import { useEffect, useState, type FormEvent } from 'react';
import type { AuthCredentials } from '../../api/auth';
import type { RegistrationMode } from '../../types/api';
import { Icon } from '../../shared/Icon';

interface Props {
  registrationMode: RegistrationMode;
  initialMode?: 'login' | 'register';
  googleAuthEnabled?: boolean;
  onClose?: () => void;
  serviceError: string | null;
  onAuthenticate: (mode: 'login' | 'register', credentials: AuthCredentials) => Promise<void>;
}

export function AuthGate({ registrationMode, initialMode = 'login', googleAuthEnabled = false, onClose, serviceError, onAuthenticate }: Props) {
  const [mode, setMode] = useState<'login' | 'register'>(initialMode === 'register' && registrationMode === 'closed' ? 'login' : initialMode);
  const [error, setError] = useState<string | null>(serviceError);
  const [submitting, setSubmitting] = useState(false);
  const registering = mode === 'register';

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setSubmitting(true);
    setError(null);
    try {
      await onAuthenticate(mode, {
        email: String(data.get('email') || ''),
        password: String(data.get('password') || ''),
        displayName: registering ? String(data.get('displayName') || '') : undefined,
        inviteCode: registering ? String(data.get('inviteCode') || '') : undefined
      });
      form.reset();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Noe gikk galt.');
    } finally {
      setSubmitting(false);
    }
  }

  function selectMode(next: 'login' | 'register') {
    setMode(next);
    setError(null);
  }

  return <section className="auth-gate" aria-labelledby="authTitle">
    <div className="auth-panel">
      {onClose && <button className="auth-close" onClick={onClose} aria-label="Tilbake til forsiden"><Icon name="close" /></button>}
      <div className="auth-brand"><span className="brand-mark">M</span><span>Mellomrom</span></div>
      <p className="eyebrow">NORSK SOM FAKTISK SITTER</p>
      <h1 id="authTitle">{registering ? 'Start norskreisen.' : 'Velkommen tilbake.'}</h1>
      <p className="auth-intro">{registering ? 'Opprett en konto for å lagre øving og fremgang.' : 'Logg inn for å fortsette der du slapp.'}</p>
      <div className="auth-tabs" role="tablist" aria-label="Konto">
        <button type="button" className={!registering ? 'active' : ''} onClick={() => selectMode('login')} role="tab" aria-selected={!registering}>Logg inn</button>
        {registrationMode !== 'closed' && <button type="button" className={registering ? 'active' : ''} onClick={() => selectMode('register')} role="tab" aria-selected={registering}>Opprett konto</button>}
      </div>
      {googleAuthEnabled && (!registering || registrationMode === 'open') && <><a className="google-auth-button" href={`/api/auth/google/start?intent=${mode}`}><span aria-hidden="true">G</span>{registering ? 'Opprett med Google' : 'Fortsett med Google'}</a><div className="auth-divider"><span>eller</span></div></>}
      <form onSubmit={submit}>
        {registering && <label className="auth-field">Navn<input name="displayName" autoComplete="name" maxLength={60} required /></label>}
        <label className="auth-field">E-post<input name="email" type="email" autoComplete="email" required /></label>
        <label className="auth-field">Passord<input name="password" type="password" autoComplete={registering ? 'new-password' : 'current-password'} minLength={10} required /></label>
        {registering && registrationMode === 'invite' && <label className="auth-field">Invitasjonskode<input name="inviteCode" autoComplete="off" required /></label>}
        {error && <p className="auth-error" role="alert">{error}</p>}
        <button className="auth-submit" type="submit" disabled={submitting}>{registering ? 'Opprett konto' : 'Logg inn'} <Icon name="arrow" /></button>
      </form>
      <p className="auth-note">Fremgangen din lagres privat på kontoen din.</p>
    </div>
    <div className="auth-art" aria-hidden="true"><span>jo</span><span>likevel</span><span>egentlig</span><blockquote>Ord blir til språk<br />når du bruker dem.</blockquote></div>
  </section>;
}
